import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Mail, ArrowRight, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email,
        reset_base_url: window.location.origin,
      });
      setSubmitted(true);
    } catch (error) {
      // Backend returns a uniform response; show success regardless to avoid leaking accounts.
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <img
              src="/protocall-logo.png"
              alt="ProToCall"
              className="h-16 w-auto"
            />
          </Link>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Forgot Password
          </h1>
          <p className="text-slate-600 mt-2">We'll email you a link to reset it</p>
        </div>

        <Card className="bg-white border border-slate-200 shadow-lg">
          <CardContent className="pt-6">
            {submitted ? (
              <div className="text-center py-4" data-testid="forgot-password-success">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-7 h-7 text-green-600" />
                </div>
                <p className="text-slate-700">
                  If an account exists for <strong>{email}</strong>, we've sent a password reset link.
                  Please check your inbox (and spam folder). The link expires in 1 hour.
                </p>
                <Button asChild className="mt-6 bg-slate-900 hover:bg-slate-800 text-white" data-testid="back-to-login-btn">
                  <Link to="/login">Back to Sign In</Link>
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-slate-700">
                    Email Address
                  </Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-10 h-11"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold h-11"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? "Sending..." : "Send Reset Link"}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </form>
            )}

            <div className="mt-6 text-center text-sm">
              <Link to="/login" className="text-orange-500 hover:text-orange-600 font-medium" data-testid="login-link">
                ← Back to Sign In
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
